const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const ticketShowService = {
  async getTicket(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        // Try to get error message from response
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (e) {
          // If response is not JSON, use default message
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        
        // Create error object with status and data for proper error handling
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.response = {
          status: response.status,
          data: errorData
        };
        throw error;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching ticket:", error);
      throw error;
    }
  },

  async getTicketNotes(ticketId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ticketsnotes/ticket/${ticketId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching ticket notes:", error);
      throw error;
    }
  },

  async deleteTicket(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete ticket");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting ticket:", error);
      throw error;
    }
  },

  // Download document
  async downloadDocument(documentId, fileName) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/documents/${documentId}/download`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading document:", error);
      throw error;
    }
  },

  // Play video/audio document (opens in new tab with inline content)
  getPlayUrl(documentId) {
    const token = localStorage.getItem("token");
    return `${API_BASE_URL}/api/documents/${documentId}/download?inline=true&token=${token}`;
  },

  // Check if file is a video type
  isVideoFile(fileName) {
    if (!fileName) return false;
    const ext = fileName.split('.').pop().toLowerCase();
    return ['mp4', 'webm', 'mov', 'avi', 'wmv', 'flv', 'm4v', 'mkv', '3gp'].includes(ext);
  },

  // Delete document with related records
  async deleteDocument(documentId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/documents/${documentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete document");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  },

  // Delete note with related records
  async deleteNote(noteId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/ticketsnotes/${noteId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete note");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error deleting note:", error);
      throw error;
    }
  },

  // Mock data for development/fallback
  getMockTicketData() {
    return {
      id: 4,
      client_view: true,
      guardian_view: true,
      reportedBy: "system", // 'system' or 'staff'
      systemSource: "Automated Monitoring System",
      property: {
        name: "Sample Property",
        property_id: "PROP001",
        postcode: "SW1A 1AA",
        address_1: "10 Downing Street",
        address_2: "Westminster",
        address_3: "London",
      },
      ticketable_type: "App\\Properties\\Property",
      ticketable_id: 1,
      type: { name: "Maintenance" },
      urgency: { name: "High" },
      status: { name: "In Progress", color: "#ffc107" },
      description:
        "Sample ticket description with detailed information about the maintenance issue. This is a comprehensive description that includes all the necessary details for understanding the problem.",
      internal_note:
        "Internal note for staff members only. This contains sensitive information that should not be visible to clients or guardians.",
      equipment_purchased:
        "New equipment purchased for this ticket including replacement parts and tools.",
      cost_of_works: "150.00",
      guardian: {
        user: {
          fullName: "John Doe",
          phone_number: "+44 20 7123 4567",
          email: "john.doe@example.com",
        },
      },
      member: null,
      assignedMember: {
        first_name: "Jane",
        last_name: "Smith",
        phone_number: "+44 20 7123 4568",
        email: "jane.smith@example.com",
      },
      contractor: {
        name: "ABC Contractors Ltd",
      },
      contractorContact: {
        department: "Maintenance Department",
        description: "Senior Maintenance Coordinator",
        user: {
          first_name: "Bob",
          last_name: "Wilson",
          job_title: "Senior Maintenance Coordinator",
          phone_number: "+44 20 7123 4569",
          mobile_number: "+44 7700 900000",
          email: "bob.wilson@abccontractors.com",
          address_1: "123 Contractor Street",
          address_2: "Industrial Estate",
          address_3: "London",
          postcode: "E1 6AN",
        },
      },
      history: [
        {
          contractorContact: {
            user: { fullName: "Bob Wilson" },
          },
          status: "Accepted",
          updated_at: "2024-01-15T10:30:00Z",
        },
        {
          contractorContact: {
            user: { fullName: "Bob Wilson" },
          },
          status: "In Progress",
          updated_at: "2024-01-18T14:20:00Z",
        },
        {
          contractorContact: {
            user: { fullName: "Bob Wilson" },
          },
          status: "Completed",
          updated_at: "2024-01-25T16:45:00Z",
        },
      ],
      created_at: "2024-01-10T09:00:00Z",
      book_in_date: "2024-01-20T14:00:00Z",
      book_in_date_to: "2024-01-20T16:00:00Z",
      date_completed: "2024-01-25T17:00:00Z",
      documents: [
        {
          id: 1,
          name: "Invoice.pdf",
          created_at: "2024-01-15T11:00:00Z",
          visible_to_guardian: true,
        },
        {
          id: 2,
          name: "Work_Report.docx",
          created_at: "2024-01-20T16:30:00Z",
          visible_to_guardian: false,
        },
        {
          id: 3,
          name: "Before_Photos.jpg",
          created_at: "2024-01-21T09:15:00Z",
          visible_to_guardian: true,
        },
      ],
    };
  },

  getMockNotesData() {
    return [
      {
        id: 1,
        description:
          "Initial assessment completed. Issue identified and contractor contacted. The problem appears to be related to the plumbing system in the kitchen area.",
        updated_at: "2024-01-11T10:00:00Z",
        user: { fullName: "Jane Smith" },
      },
      {
        id: 2,
        description:
          "Contractor has accepted the job and will start work on Monday. They have confirmed they have all necessary tools and materials.",
        updated_at: "2024-01-12T14:30:00Z",
        user: { fullName: "Bob Wilson" },
      },
      {
        id: 3,
        description:
          "Work started as scheduled. Initial inspection reveals the issue is more complex than initially thought. Will need additional parts.",
        updated_at: "2024-01-20T15:45:00Z",
        user: { fullName: "Bob Wilson" },
      },
      {
        id: 4,
        description:
          "Additional parts ordered and received. Work should be completed by end of day tomorrow.",
        updated_at: "2024-01-22T11:20:00Z",
        user: { fullName: "Bob Wilson" },
      },
    ];
  },
};

export default ticketShowService;
